if (!customElements.get("product-feed")) {
  customElements.define(
    "product-feed",
    class ProductFeed extends HTMLElement {
      constructor() {
        super();
        this.$selectors = {
          activeFetcher: `[data-active-fetcher="true"]`,
          activeSublinkList: `[data-active-sublink-list="true"]`,
          sublinkList: `[data-sublink-list-handle]`,
          sublinkListSlider: `._sublinkListSlider`,
          dataFeedUrl: `[data-feed-url]`,
          feed: `._feed`,
          feedBlock: `._feedBlock`,
          sublink: `._sublink`,
          fetcherListMobile: `._fetcherListMobile`,
          skeletonFeed: `._skeletonFeed`,
          emptyCollection: `._emptyCollection`,
        };
        this.$sectionName = this.dataset.sectionName;
      }

      connectedCallback() {
        delegate(this, "pagination:page-changed", this.reRenderFeed.bind(this));
        delegate(this, "filters:filter-changed", this.reRenderFeed.bind(this));

        // define fixed nodes
        this.$feed = this.querySelector(this.$selectors.feed);
        this.$feedFetchers = this.querySelectorAll(this.$selectors.dataFeedUrl);
        this.$sublinkLists = this.querySelectorAll(this.$selectors.sublinkList);
        this.$sublinks = this.querySelectorAll(this.$selectors.sublink);
        this.$skeletonFeed = this.querySelector(this.$selectors.skeletonFeed);

        // add listeners to fetchers
        this.$feedFetchers.forEach((link) =>
          link.addEventListener("click", (e) => {
            this.onFetcherClick(e.currentTarget);
          })
        );

        // dynamic feed functions are gathered in one function for easy re-run
        this.onFeedChange();
      }

      onFeedChange() {
        // define dynamic nodes
        this.$activeFetcher = this.querySelector(this.$selectors.activeFetcher);
        this.$activeSublinkList = this.querySelector(
          this.$selectors.activeSublinkList
        );
        this.$feedBlocks = this.querySelectorAll(this.$selectors.feedBlock);

        // cache feed
        window.feeds = window.feeds || {};
        window.feeds[this.$activeFetcher.dataset.feedUrl] =
          window.feeds[this.$activeFetcher.dataset.feedUrl] ||
          this.$feed.innerHTML;

        // manage sublink highlighters on scroll
        const callback = (entries) => {
          entries.forEach((entry) => {
            let activeTargetId;

            // see if scrolling down or up
            const scrollingDown = window.scrollY > this.previousScroll;

            // reset previousScroll to current scroll so we can use it on next intersection
            this.previousScroll = window.scrollY;

            // if is scrolling down and the entry has left the viewport, then the next sibling is active
            if (scrollingDown && !entry.isIntersecting)
              activeTargetId = entry.target.nextElementSibling
                ? entry.target.nextElementSibling.id
                : null;

            // if is scrolling up and the entry entered the viewport, entry is active
            if (!scrollingDown && entry.isIntersecting)
              activeTargetId = entry.target.id;

            // if none of the above scenarios is true, then do nothing
            if (!activeTargetId) return;

            // reset previously active sublinks
            this.$sublinks.forEach(
              (sublink) => (sublink.dataset.activeSublink = false)
            );

            // find sublinks to be active via matching their hashes with activeTargetId
            // (sublinks -> plural because mobile and desktop sublinklists are different)
            const activeSublinks = [...this.$sublinks].filter(
              (link) => link.hash === `#${activeTargetId}`
            );
            activeSublinks.forEach(
              (link) => (link.dataset.activeSublink = true)
            );
          });
        };

        const intersectionObserver = new IntersectionObserver(callback, {
          rootMargin: "-150px 0px 0px 0px",
        });
        this.$feedBlocks.forEach((block) =>
          intersectionObserver.observe(block)
        );
      }

      async reRenderFeed(event, pushState = false) {
        const url = new URL(window.location.pathname, window.location.origin);
        if (pushState) {
          history.replaceState({}, "", url + "?" + event.detail.searchParams);
        }

        this.$feed.innerHTML = await this.fetchFeed(
          url,
          event.detail.searchParams
        );

        // display actual feed, hide skeleton loader
        this.$feed.classList.remove("hidden");
        this.$skeletonFeed.classList.add("hidden");

        this.onFeedChange();
      }

      async onFetcherClick(clickedFetcher, force = false) {
        // do nothing if active link is clicked
        if (clickedFetcher === this.$activeFetcher && !force) return;

        // scroll to top of the feed
        // this.scrollIntoView({ behavior: "instant" });
        // if an another fetchFeed function is running in the background, abort it
        window.fetchController?.abort();

        // clean highlight on previous sublinks
        this.$sublinks.forEach(
          (sublink) => (sublink.dataset.activeSublink = false)
        );

        // reset activeSublinkList
        this.$sublinkLists.forEach((list) => {
          list.dataset.activeSublinkList =
            list.dataset.sublinkListHandle === clickedFetcher.dataset.handle;
        });

        // reset activeFetcher
        this.$feedFetchers.forEach((fetcher) => {
          fetcher.dataset.activeFetcher = fetcher === clickedFetcher;
          if (fetcher === clickedFetcher) {
            if (fetcher.open) {
              fetcher.open();
            }
          } else {
            if (fetcher.close) {
              fetcher.close();
            }
          }
        });

        // get mobile fetcher button and horizontally center it
        const fetcherListMobile = this.querySelector(
          `${this.$selectors.fetcherListMobile}`
        );
        const activeFetcherMobile = this.querySelector(
          `${this.$selectors.fetcherListMobile} [data-feed-url="${clickedFetcher.dataset.feedUrl}"]`
        );
        fetcherListMobile.scrollTo({
          behavior: "smooth",
          left:
            activeFetcherMobile.offsetLeft -
            (fetcherListMobile.clientWidth - activeFetcherMobile.offsetWidth) /
              2,
        });

        // show hide correct mobile fetcher sub list

        // load feed
        if (!force && window.feeds[clickedFetcher.dataset.feedUrl]) {
          this.$feed.innerHTML = window.feeds[clickedFetcher.dataset.feedUrl];
        } else {
          let childFeedUrls = this.querySelectorAll(
            `.product-feed--nav-mobile [data-active-sublink-list="true"] [data-child-feed-url]`
          );
          if (childFeedUrls.length > 0) {
            for (let i = 0; i < childFeedUrls.length; i++) {
              let childFeedUrl = childFeedUrls[i];
              if (i === 0) {
                this.$feed.innerHTML = await this.fetchFeed(
                  childFeedUrl.dataset.childFeedUrl
                );
              } else {
                // display actual feed, hide skeleton loader
                this.$feed.classList.remove("hidden");
                this.$skeletonFeed.classList.add("hidden");
                this.$feed.innerHTML += await this.fetchFeed(
                  childFeedUrl.dataset.childFeedUrl
                );
              }
            }
          } else {
            this.$feed.innerHTML = await this.fetchFeed(
              clickedFetcher.dataset.feedUrl
            );
          }
        }

        // display actual feed, hide skeleton loader
        this.$feed.classList.remove("hidden");
        this.$skeletonFeed.classList.add("hidden");

        // functions to be run after feed change
        this.onFeedChange();
      }

      onSublinkClick(sublink) {
        this.onFetcherClick(sublink.closest(this.$selectors.feedUrl)).then(
          () => {
            document.querySelector(sublink.hash).scrollIntoView();
          }
        );
      }

      async fetchFeed(url, params) {
        this.$skeletonFeed.classList.remove("hidden");
        this.$feed.classList.add("hidden");

        window.fetchController = new AbortController();
        const { signal } = fetchController;

        const fetchUrl = `${url}?section_id=${this.$sectionName}${params ? `&${params}` : ""}`

          console.log(fetchUrl)
        const feedHTML = await fetch(fetchUrl,
          {
            signal,
          }
        )
          .then((response) => {
            if (!response.ok) {
              var error = new Error(response);
              throw error;
            }
            return response.text();
          })
          .then((html) => {
            const parsed = new DOMParser().parseFromString(html, "text/html");
            let feed = parsed.querySelector(this.$selectors.feed);
            if(!feed) {
              feed = parsed.querySelector(this.$selectors.emptyCollection);
            }

            return feed.innerHTML
          });
        return feedHTML;
      }
    }
  );
}
